import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SearchIcon from "@mui/icons-material/Search";
import Webcam from "react-webcam";
import api from "../Api";

const safeStr = (value) => (value === null || value === undefined ? "" : String(value));

export default function SkuImageLookupDialog({ open, onClose, selectedAccount = "" }) {
  const [mode, setMode] = useState("tracking");
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMessage, setCameraMessage] = useState("");
  const inputRef = useRef(null);
  const webcamRef = useRef(null);
  const cameraBusyRef = useRef(false);

  const stopCameraTracks = useCallback(() => {
    const stream = webcamRef.current?.video?.srcObject;
    stream?.getTracks?.().forEach(track => track.stop());
  }, []);

  const runLookup = useCallback(async (lookupValue = value, lookupMode = mode) => {
    const q = safeStr(lookupValue).trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get("/api/sku-image-lookup", {
        params: {
          q,
          type: lookupMode,
          account_name: selectedAccount,
        },
      });
      setResult(data);
      if (data?.found === false) setError(data.message || "No match found");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Lookup failed");
    } finally {
      setLoading(false);
    }
  }, [mode, selectedAccount, value]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setShowCamera(false);
      setCameraMessage("");
      return;
    }
    return stopCameraTracks;
  }, [open, stopCameraTracks]);

  useEffect(() => {
    if (!showCamera || mode !== "tracking") return undefined;
    if (!("BarcodeDetector" in window)) {
      setCameraMessage("Camera opened. This browser cannot auto-read barcode, use external scanner or type tracking ID.");
      return undefined;
    }

    let cancelled = false;
    let detector = null;
    cameraBusyRef.current = false;
    setCameraMessage("Point camera at tracking barcode");

    try {
      detector = new window.BarcodeDetector({
        formats: ["code_128", "code_39", "code_93", "ean_13", "ean_8", "upc_a", "upc_e", "itf", "codabar"],
      });
    } catch {
      setCameraMessage("Barcode reader could not start. Type tracking ID manually.");
      return undefined;
    }

    const timer = window.setInterval(async () => {
      const video = webcamRef.current?.video;
      if (!video || video.readyState < 2 || cameraBusyRef.current || cancelled) return;
      cameraBusyRef.current = true;
      try {
        const codes = await detector.detect(video);
        const scanned = safeStr(codes?.[0]?.rawValue).trim();
        if (scanned && !cancelled) {
          setValue(scanned);
          setShowCamera(false);
          setCameraMessage("");
          await runLookup(scanned, "tracking");
        }
      } catch (err) {
        if (!cancelled) setCameraMessage("Keep barcode inside the box");
      } finally {
        cameraBusyRef.current = false;
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [showCamera, mode, runLookup]);

  useEffect(() => {
    if (!open || mode !== "sku" || value.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/api/sku-image-suggestions", {
          params: { q: value.trim(), account_name: selectedAccount, limit: 12 },
        });
        if (!cancelled) setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, mode, value, selectedAccount]);

  const title = useMemo(() => {
    if (mode === "tracking") return "Scan tracking ID";
    return "Search SKU";
  }, [mode]);

  const chooseSuggestion = (_event, option) => {
    if (!option) return;
    const nextValue = option.real_sku || option.base_sku || "";
    setValue(nextValue);
    runLookup(nextValue, "sku");
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCameraMessage("");
    stopCameraTracks();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Product Image Check</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_event, next) => {
              if (!next) return;
              setMode(next);
              setValue("");
              setResult(null);
              setError("");
              setSuggestions([]);
              closeCamera();
              setTimeout(() => inputRef.current?.focus(), 80);
            }}
          >
            <ToggleButton value="tracking">Tracking ID</ToggleButton>
            <ToggleButton value="sku">SKU</ToggleButton>
          </ToggleButtonGroup>

          {mode === "sku" ? (
            <Autocomplete
              freeSolo
              options={suggestions}
              loading={loading}
              getOptionLabel={(option) => typeof option === "string" ? option : (option.real_sku || option.base_sku || "")}
              onInputChange={(_event, next) => setValue(next)}
              onChange={chooseSuggestion}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {option.image_url ? (
                    <Box component="img" src={option.image_url} alt="" sx={{ width: 38, height: 38, objectFit: "contain", border: "1px solid #ddd", borderRadius: 1 }} />
                  ) : (
                    <Box sx={{ width: 38, height: 38, border: "1px solid #ddd", borderRadius: 1 }} />
                  )}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.base_sku}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.real_sku} {option.account_name ? `- ${option.account_name}` : ""}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  label={title}
                  placeholder="Type SKU"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      runLookup(value, "sku");
                    }
                  }}
                  fullWidth
                />
              )}
            />
          ) : (
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  inputRef={inputRef}
                  label={title}
                  placeholder="Scan or enter tracking ID"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      runLookup(value, "tracking");
                    }
                  }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  color={showCamera ? "warning" : "primary"}
                  onClick={() => {
                    if (showCamera) closeCamera();
                    else setShowCamera(true);
                  }}
                  sx={{ minWidth: 52, height: 56 }}
                  aria-label={showCamera ? "Close camera" : "Open camera"}
                >
                  <CameraAltIcon />
                </Button>
              </Box>

              {showCamera && (
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 220, sm: 320 },
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "#111",
                  }}
                >
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                      facingMode: { ideal: "environment" },
                      width: { ideal: 1920 },
                      height: { ideal: 1080 },
                    }}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "28%",
                      left: "10%",
                      width: "80%",
                      height: "44%",
                      border: "3px dashed #fff",
                      borderRadius: 1,
                      pointerEvents: "none",
                    }}
                  />
                  {cameraMessage && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 8,
                        right: 8,
                        bottom: 8,
                        bgcolor: "rgba(0,0,0,0.68)",
                        color: "#fff",
                        px: 1,
                        py: 0.75,
                        borderRadius: 1,
                        fontSize: 13,
                        textAlign: "center",
                      }}
                    >
                      {cameraMessage}
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          )}

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => runLookup(value, mode)}
            disabled={loading || !value.trim()}
          >
            {loading ? "Checking..." : "Check Image"}
          </Button>

          {error && <Alert severity="error">{error}</Alert>}

          {result && result.found !== false && (
            <Box sx={{ border: "1px solid #ddd", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
              <Box sx={{ height: { xs: 260, sm: 360 }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fafafa", p: 1 }}>
                {result.image_url ? (
                  <Box component="img" src={result.image_url} alt={result.base_sku || result.real_sku} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <Typography color="text.secondary">Image not fetched yet</Typography>
                )}
              </Box>
              <Stack spacing={1} sx={{ p: 2 }}>
                <Typography variant="h6">{result.base_sku || result.real_sku}</Typography>
                <Typography variant="body2"><b>Real SKU:</b> {result.real_sku || "-"}</Typography>
                {result.tracking_id && <Typography variant="body2"><b>Tracking:</b> {result.tracking_id}</Typography>}
                <Typography variant="body2"><b>Account:</b> {result.account_name || "-"}</Typography>
                <Typography variant="body2"><b>Image From:</b> {result.image_account_name || "-"}</Typography>
                <Typography variant="body2"><b>FSN:</b> {result.fsn || "-"}</Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Chip size="small" label={result.input_type || "lookup"} />
                  {result.size_suffix && <Chip size="small" label={`Size ${result.size_suffix}`} />}
                  <Chip size="small" color={result.has_image ? "success" : "warning"} label={result.has_image ? "Image found" : "No image"} />
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
